// Computed data for charts
export const CHART_PAGE_SIZE = 8;

export const TREND_WINDOW_SIZES = {
  daily: 7,
  weekly: 4,
  monthly: 12,
};

const MONTH_YEAR_RE = /\b(?:19|20)\d{2}\b/;

export function searchByName(items, query) {
  const rows = Array.isArray(items) ? items : [];
  const q = String(query ?? "").trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((item) => String(item?.name ?? "").toLowerCase().includes(q));
}

export function paginate(items, page, pageSize = CHART_PAGE_SIZE) {
  const rows = Array.isArray(items) ? items : [];
  const size = Math.max(1, Number(pageSize) || CHART_PAGE_SIZE);
  const total = rows.length;
  const totalPages = Math.max(1, Math.ceil(total / size) || 1);
  const safePage = Math.min(Math.max(1, Number(page) || 1), totalPages);
  const startIndex = total === 0 ? 0 : (safePage - 1) * size;
  const end = Math.min(startIndex + size, total);

  return {
    items: rows.slice(startIndex, end),
    page: safePage,
    pageSize: size,
    total,
    totalPages,
    start: total === 0 ? 0 : startIndex + 1,
    end,
    hasPrev: safePage > 1 && total > 0,
    hasNext: safePage < totalPages && total > 0,
  };
}

export function getTrendWindow(data, windowSize, pageFromEnd = 0) {
  const rows = Array.isArray(data) ? data : [];
  const size = Math.max(1, Number(windowSize) || 1);
  const totalPages = Math.max(1, Math.ceil(rows.length / size) || 1);
  const page = Math.min(Math.max(0, Number(pageFromEnd) || 0), totalPages - 1);
  const end = rows.length - page * size;
  const start = Math.max(0, end - size);

  return {
    items: rows.slice(start, end),
    page,
    totalPages,
    hasOlder: page < totalPages - 1 && rows.length > size,
    hasNewer: page > 0,
  };
}

function extractYear(period) {
  if (period?.year != null && String(period.year).trim() !== "") {
    const year = String(period.year).match(MONTH_YEAR_RE);
    if (year) return year[0];
    if (/^\d{4}$/.test(String(period.year).trim())) return String(period.year).trim();
  }

  const candidates = [period?.date, period?.start, period?.period, period?.start_date];
  for (const value of candidates) {
    const match = String(value ?? "").match(MONTH_YEAR_RE);
    if (match) return match[0];
  }

  return null;
}

export function formatMonthLabel(month) {
  const label = String(month?.label ?? month?.name ?? "").trim();
  if (MONTH_YEAR_RE.test(label)) return label;

  const year = extractYear(month);
  if (label && year) return `${label} ${year}`;
  return label;
}

export const getLeadsByClient = (clients) => {
  return clients.map((agent) => ({
    id: agent.client_id ?? agent.agent_id ?? agent.id,
    name: agent.client_name,
    leads: agent.total_leads,
  }));
};

export const getLeadsByProperty = (properties) => {
  return properties.map((property) => ({
    id: property.property_id ?? property.id,
    clientId: property.client_id ?? property.agent_id,
    name: property.property_name,
    leads: property.total_leads,
  }));
};

export const getLeadScoreDistribution = (score_distribution) => {
  const dist =
    score_distribution && !Array.isArray(score_distribution)
      ? score_distribution
      : {};

  return [
    { name: "Cold", value: Number(dist.cold) || 0, fill: "#9ca3af" },
    { name: "Warm", value: Number(dist.warm) || 0, fill: "#65a30d" },
    { name: "Hot", value: Number(dist.hot) || 0, fill: "#16a34a" },
  ];
};

export const getWeeklyLeadsTrend = (weekly) => {
  return weekly.map((week) => ({
    name: week.label,
    leads: week.count,
  }));
};

export const getMonthlyLeadsTrend = (monthly) => {
  return monthly.map((month) => ({
    name: formatMonthLabel(month),
    leads: month.count,
  }));
};

export const getDailyLeadsTrend = (daily) => {
  return daily.map((day) => ({
    name: day.label,
    leads: day.count,
  }));
};
